import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

// Create context
const ReportingContext = createContext();

// Custom hook to use reporting context
export const useReporting = () => {
  const context = useContext(ReportingContext);
  if (!context) {
    throw new Error('useReporting must be used within a ReportingProvider');
  }
  return context;
};

// Helper function to get date range
const getDateRange = (period) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (period) {
    case 'today':
      return {
        start: today,
        end: new Date(today.getTime() + 24 * 60 * 60 * 1000),
        label: 'Today'
      };
    case 'yesterday':
      return {
        start: new Date(today.getTime() - 24 * 60 * 60 * 1000),
        end: today,
        label: 'Yesterday'
      };
    case 'last7days':
      return {
        start: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000),
        end: new Date(today.getTime() + 24 * 60 * 60 * 1000),
        label: 'Last 7 Days'
      };
    case 'last30days':
      return {
        start: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000),
        end: new Date(today.getTime() + 24 * 60 * 60 * 1000),
        label: 'Last 30 Days'
      };
    case 'thisMonth':
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: new Date(today.getTime() + 24 * 60 * 60 * 1000),
        label: 'This Month'
      };
    case 'lastMonth':
      return {
        start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        end: new Date(now.getFullYear(), now.getMonth(), 1),
        label: 'Last Month'
      };
    default:
      return {
        start: today,
        end: new Date(today.getTime() + 24 * 60 * 60 * 1000),
        label: 'Today'
      };
  }
};

// Provider component
export const ReportingProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [customDateRange, setCustomDateRange] = useState({ start: null, end: null });

  // Sales metrics
  const [salesMetrics, setSalesMetrics] = useState({
    totalSales: 0,
    totalOrders: 0,
    deliveredOrders: 0,
    averageOrderValue: 0,
    previousPeriodSales: 0,
    previousPeriodOrders: 0,
    salesGrowth: 0,
    ordersGrowth: 0
  });

  // Customer experience metrics
  const [customerMetrics, setCustomerMetrics] = useState({
    averageRating: 0,
    totalReviews: 0,
    rejectedOrders: 0,
    rejectedPercentage: 0,
    delayedOrders: 0,
    delayedPercentage: 0,
    poorRatedOrders: 0,
    poorRatedPercentage: 0,
    totalComplaints: 0,
    lostSales: 0,
    onlinePercentage: 100
  });

  // Customer funnel metrics
  const [funnelMetrics, setFunnelMetrics] = useState({
    menuViews: 0,
    addedToCart: 0,
    checkoutInitiated: 0,
    ordersPlaced: 0,
    menuToCartRate: 0,
    cartToCheckoutRate: 0,
    checkoutToOrderRate: 0
  });

  // Chart data
  const [chartData, setChartData] = useState({
    revenueOverTime: [],
    ordersByDay: [],
    topSellingItems: [],
    peakHours: [],
    ordersByStatus: [],
    paymentMethods: []
  });

  // Fetch sales data
  const fetchSalesData = useCallback(async (startDate, endDate) => {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef,
        where('createdAt', '>=', Timestamp.fromDate(startDate)),
        where('createdAt', '<', Timestamp.fromDate(endDate)),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const orders = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate metrics
      const deliveredOrders = orders.filter(o => o.status === 'delivered');
      const totalSales = deliveredOrders.reduce((sum, o) => sum + (o.totalAmount || o.total || 0), 0);
      const cancelledOrders = orders.filter(o => o.status === 'cancelled');

      // Calculate average order value
      const aov = deliveredOrders.length > 0 ? totalSales / deliveredOrders.length : 0;

      // Calculate lost sales from cancelled orders
      const lostSales = cancelledOrders.reduce((sum, o) => sum + (o.totalAmount || o.total || 0), 0);

      return {
        orders,
        totalSales,
        totalOrders: orders.length,
        deliveredOrders: deliveredOrders.length,
        averageOrderValue: Math.round(aov),
        cancelledOrders: cancelledOrders.length,
        lostSales
      };
    } catch (err) {
      console.error('Error fetching sales data:', err);
      return null;
    }
  }, []);

  // Fetch previous period data for comparison
  const fetchPreviousPeriodData = useCallback(async (currentStart, currentEnd) => {
    const periodLength = currentEnd.getTime() - currentStart.getTime();
    const previousStart = new Date(currentStart.getTime() - periodLength);
    const previousEnd = currentStart;

    return fetchSalesData(previousStart, previousEnd);
  }, [fetchSalesData]);

  // Fetch reviews data
  const fetchReviewsData = useCallback(async (startDate, endDate) => {
    try {
      const reviewsRef = collection(db, 'reviews');
      const q = query(
        reviewsRef,
        where('createdAt', '>=', Timestamp.fromDate(startDate)),
        where('createdAt', '<', Timestamp.fromDate(endDate))
      );

      const snapshot = await getDocs(q);
      const reviews = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const totalReviews = reviews.length;
      const totalRating = reviews.reduce((sum, r) => sum + (r.rating || 0), 0);
      const averageRating = totalReviews > 0 ? (totalRating / totalReviews).toFixed(1) : 0;
      const poorRatedOrders = reviews.filter(r => r.rating <= 2).length;

      return {
        totalReviews,
        averageRating: parseFloat(averageRating),
        poorRatedOrders
      };
    } catch (err) {
      console.error('Error fetching reviews:', err);
      return { totalReviews: 0, averageRating: 0, poorRatedOrders: 0 };
    }
  }, []);

  // Generate chart data
  const generateChartData = useCallback((orders, startDate, endDate) => {
    // Revenue over time (daily)
    const dailyRevenue = {};
    const dailyOrders = {};
    const hourlyOrders = Array(24).fill(0);
    const itemSales = {};
    const statusCount = {};
    const paymentCount = {};

    orders.forEach(order => {
      // Get date string
      const orderDate = order.createdAt?.toDate?.() || new Date(order.createdAt);
      const dateStr = orderDate.toISOString().split('T')[0];
      const hour = orderDate.getHours();

      // Daily revenue
      if (order.status === 'delivered') {
        dailyRevenue[dateStr] = (dailyRevenue[dateStr] || 0) + (order.totalAmount || order.total || 0);
      }

      // Daily orders
      dailyOrders[dateStr] = (dailyOrders[dateStr] || 0) + 1;

      // Hourly distribution
      hourlyOrders[hour]++;

      // Item sales
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          const itemName = item.name || item.itemName;
          if (itemName) {
            itemSales[itemName] = (itemSales[itemName] || 0) + (item.quantity || 1);
          }
        });
      }

      // Status count
      statusCount[order.status] = (statusCount[order.status] || 0) + 1;

      // Payment method count
      const paymentMethod = order.paymentMethod || 'cod';
      paymentCount[paymentMethod] = (paymentCount[paymentMethod] || 0) + 1;
    });

    // Convert to array format for charts
    const revenueOverTime = Object.entries(dailyRevenue)
      .map(([date, amount]) => ({
        date,
        revenue: amount,
        displayDate: new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const ordersByDay = Object.entries(dailyOrders)
      .map(([date, count]) => ({
        date,
        orders: count,
        displayDate: new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const topSellingItems = Object.entries(itemSales)
      .map(([name, quantity]) => ({ name, quantity }))
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10);

    const peakHours = hourlyOrders.map((count, hour) => ({
      hour: `${hour}:00`,
      orders: count,
      displayHour: hour < 12 ? `${hour === 0 ? 12 : hour} AM` : `${hour === 12 ? 12 : hour - 12} PM`
    }));

    const ordersByStatus = Object.entries(statusCount)
      .map(([status, count]) => ({
        status: status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' '),
        count
      }));

    const paymentMethods = Object.entries(paymentCount)
      .map(([method, count]) => ({
        method: method === 'cod' ? 'Cash on Delivery' : method === 'online' ? 'Online Payment' : method,
        count
      }));

    return {
      revenueOverTime,
      ordersByDay,
      topSellingItems,
      peakHours,
      ordersByStatus,
      paymentMethods
    };
  }, []);

  // Main fetch function
  const fetchReportingData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const dateRange = customDateRange.start && customDateRange.end
        ? customDateRange
        : getDateRange(selectedPeriod);

      // Fetch current period data
      const currentData = await fetchSalesData(dateRange.start, dateRange.end);

      if (!currentData) {
        throw new Error('Failed to fetch sales data');
      }

      // Fetch previous period data for comparison
      const previousData = await fetchPreviousPeriodData(dateRange.start, dateRange.end);

      // Fetch reviews
      const reviewsData = await fetchReviewsData(dateRange.start, dateRange.end);

      // Calculate growth percentages
      const salesGrowth = previousData && previousData.totalSales > 0
        ? Math.round(((currentData.totalSales - previousData.totalSales) / previousData.totalSales) * 100)
        : 0;

      const ordersGrowth = previousData && previousData.totalOrders > 0
        ? Math.round(((currentData.totalOrders - previousData.totalOrders) / previousData.totalOrders) * 100)
        : 0;

      // Update sales metrics
      setSalesMetrics({
        totalSales: currentData.totalSales,
        totalOrders: currentData.totalOrders,
        deliveredOrders: currentData.deliveredOrders,
        averageOrderValue: currentData.averageOrderValue,
        previousPeriodSales: previousData?.totalSales || 0,
        previousPeriodOrders: previousData?.totalOrders || 0,
        salesGrowth,
        ordersGrowth
      });

      // Update customer metrics
      setCustomerMetrics({
        averageRating: reviewsData.averageRating,
        totalReviews: reviewsData.totalReviews,
        rejectedOrders: currentData.cancelledOrders,
        rejectedPercentage: currentData.totalOrders > 0
          ? Math.round((currentData.cancelledOrders / currentData.totalOrders) * 100)
          : 0,
        delayedOrders: 0, // Would need order completion time tracking
        delayedPercentage: 0,
        poorRatedOrders: reviewsData.poorRatedOrders,
        poorRatedPercentage: reviewsData.totalReviews > 0
          ? Math.round((reviewsData.poorRatedOrders / reviewsData.totalReviews) * 100)
          : 0,
        totalComplaints: 0, // Would need complaints collection
        lostSales: currentData.lostSales,
        onlinePercentage: 100 // Would need shop status tracking
      });

      // Generate chart data
      const charts = generateChartData(currentData.orders, dateRange.start, dateRange.end);
      setChartData(charts);

      // Estimate funnel metrics (would need actual tracking)
      const estimatedMenuViews = currentData.totalOrders * 5; // Rough estimate
      const estimatedAddToCart = currentData.totalOrders * 2;
      const estimatedCheckout = Math.round(currentData.totalOrders * 1.3);

      setFunnelMetrics({
        menuViews: estimatedMenuViews,
        addedToCart: estimatedAddToCart,
        checkoutInitiated: estimatedCheckout,
        ordersPlaced: currentData.totalOrders,
        menuToCartRate: estimatedMenuViews > 0 ? Math.round((estimatedAddToCart / estimatedMenuViews) * 100) : 0,
        cartToCheckoutRate: estimatedAddToCart > 0 ? Math.round((estimatedCheckout / estimatedAddToCart) * 100) : 0,
        checkoutToOrderRate: estimatedCheckout > 0 ? Math.round((currentData.totalOrders / estimatedCheckout) * 100) : 0
      });

    } catch (err) {
      console.error('Error fetching reporting data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, customDateRange, fetchSalesData, fetchPreviousPeriodData, fetchReviewsData, generateChartData]);

  // Export report data as CSV
  const exportReportCSV = useCallback((reportType) => {
    let csvContent = '';
    let filename = '';

    switch (reportType) {
      case 'sales':
        csvContent = 'Date,Revenue\n' +
          chartData.revenueOverTime.map(d => `${d.date},${d.revenue}`).join('\n');
        filename = 'sales_report.csv';
        break;
      case 'orders':
        csvContent = 'Date,Orders\n' +
          chartData.ordersByDay.map(d => `${d.date},${d.orders}`).join('\n');
        filename = 'orders_report.csv';
        break;
      case 'items':
        csvContent = 'Item,Quantity Sold\n' +
          chartData.topSellingItems.map(d => `"${d.name}",${d.quantity}`).join('\n');
        filename = 'items_report.csv';
        break;
      default:
        return;
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }, [chartData]);

  // Fetch data when period changes
  useEffect(() => {
    fetchReportingData();
  }, [fetchReportingData]);

  const value = {
    loading,
    error,
    selectedPeriod,
    setSelectedPeriod,
    customDateRange,
    setCustomDateRange,
    salesMetrics,
    customerMetrics,
    funnelMetrics,
    chartData,
    fetchReportingData,
    exportReportCSV,
    getDateRange
  };

  return (
    <ReportingContext.Provider value={value}>
      {children}
    </ReportingContext.Provider>
  );
};

export default ReportingContext;
